import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import BasePageUAM from '../BasePageUAM/BasePageUAM';
import Step1RegistrationPanel from './Panels/Step1RegistrationPanel/Step1RegistrationPanel';
import EmailPendingPanel from '../../shared-panels/uam/EmailPendingPanel/EmailPendingPanel';
import RegistrationCompletePanel from '../../shared-panels/uam/RegistrationCompletePanel/RegistrationCompletePanel';
import { makeInitialField, setFieldAndValidate } from '../../utils/field-utils';
import { userFieldSchemas } from '../../utils/validation-utils';
import { composeDestinationQuerystring } from '../../../shared-with-server/destination-utils';
import { destinationInfo } from '../../destructured-front-end-config';
import { getLocalStorageRegistrationInfo, multiStepResultTypes, processMultiStepRegistrationEntry } from '../../utils/uam-utils';
import BusyOverlay from '../../shared-panels/uam/BusyOverlay/BusyOverlay';

const RegistrationStep1Page = ({ brand, destPath }) => {
  const isNewRegistration = useRef(localStorage.getItem('is_new_registration') === 'true');

  const [isBusy, setIsBusy] = useState(!isNewRegistration.current);
  const [emailPending, setEmailPending] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [firstName, setFirstName] = useState(makeInitialField(''));
  const [lastName, setLastName] = useState(makeInitialField(''));
  const [countryOrRegion, setCountryOrRegion] = useState(makeInitialField(''));
  const [role, setRole] = useState(makeInitialField(''));
  const [roleType, setRoleType] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [school, setSchool] = useState(makeInitialField(''));
  const [discipline, setDiscipline] = useState(makeInitialField(''));
  const [availableDisciplines, setAvailableDisciplines] = useState([]);

  // empty effect to load data if not new registration and process results (only results that do NOT depend on role data)
  useEffect(() => {
    if (isNewRegistration.current) {
      const step1Data = getLocalStorageRegistrationInfo('step1');

      if (step1Data?.firstName?.length) {
        setFieldAndValidate(firstName, setFirstName, step1Data.firstName, userFieldSchemas.firstName);
      }

      if (step1Data?.lastName?.length) {
        setFieldAndValidate(lastName, setLastName, step1Data.lastName, userFieldSchemas.lastName);
      }

      if (step1Data?.country?.length) {
        setFieldAndValidate(countryOrRegion, setCountryOrRegion, step1Data.country, userFieldSchemas.countryOrRegion);
      }
    }

    axios.get('/api/v2/fi/user-roles').then((res) => {
      setAvailableRoles(res.data);
    });

    axios.get('/api/v2/fi/instructor-disciplines').then((res) => {
      setAvailableDisciplines(res.data);
    });
  }, []);

  // role data watcher to carry out update (from server or local storage) for parts that depend on role and discipline data
  useEffect(() => {
    if (!availableRoles.length || !availableDisciplines.length) { return; }

    if (isBusy) {
      setIsBusy(false);
    }

    if (isNewRegistration.current) {
      const step1Data = getLocalStorageRegistrationInfo('step1');

      if (step1Data?.role?.length) {
        const matchedRole = availableRoles.find(role => role.value === step1Data.role);
        setFieldAndValidate(role, setRole, matchedRole.value, userFieldSchemas.role);
        setRoleType(matchedRole.type);
      }

      if (step1Data?.institutionId?.length) {
        const schoolData = {
          institution: step1Data.school,
          institutionId: step1Data.institutionId,
          cAccount: step1Data.cAccount,
        };

        setFieldAndValidate(
          school,
          setSchool,
          schoolData,
          userFieldSchemas.school,
        );
      }

      if (step1Data?.discipline?.length) {
        setFieldAndValidate(discipline, setDiscipline, step1Data.discipline, userFieldSchemas.discipline);
      }
    } else {
      axios.get(
        '/api/v2/fi/multi-step-registration',
        { validateStatus: (status) => status < 500 }
      ).then((res) => {
        if (res.status === 404) { // user not found
          return;
        }

        if (res.status === 401) {
          axios.post(`/api/v2/fi/multi-step-registration-entry?${composeDestinationQuerystring(destinationInfo, destPath)}`, {
            email: getLocalStorageRegistrationInfo('email'),
          }, { validateStatus: (status) => status < 500 }).then((res) => {
            const result = processMultiStepRegistrationEntry(res.status, res.data, setEmailPending, setRegistrationComplete);

            if (result.status === multiStepResultTypes.error) {
              alert('An unexpected error occurred getting registration data. You will need to start over.');
              console.error('Error getting registration data', res);
              window.location.assign(`/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`);
            }
          });

          return;
        }

        if (res.status !== 200) {
          console.error('Error getting multi-step registration data', res);
          alert('An unexpected error occurred getting registration data. You will need to start over.');

          window.location.assign(`/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`);
          return;
        }

        const step1Data = res.data.step1;

        setFieldAndValidate(firstName, setFirstName, step1Data.firstName, userFieldSchemas.firstName);
        setFieldAndValidate(lastName, setLastName, step1Data.lastName, userFieldSchemas.lastName);
        setFieldAndValidate(countryOrRegion, setCountryOrRegion, step1Data.country, userFieldSchemas.countryOrRegion);
        const matchedRole = availableRoles.find(role => role.value === step1Data.role);
        setFieldAndValidate(role, setRole, matchedRole.value, userFieldSchemas.role);
        setRoleType(matchedRole.type);
        setFieldAndValidate(
          school,
          setSchool,
          {
            institution: step1Data.school,
            institutionId: step1Data.institutionId,
            cAccount: step1Data.cAccount,
          },
          userFieldSchemas.school,
        );
        setFieldAndValidate(discipline, setDiscipline, step1Data.discipline, userFieldSchemas.discipline);
      });
    }
  }, [availableRoles, availableDisciplines]);

  const getPanel = () => {
    if (emailPending) {
      return <EmailPendingPanel email={email.value} />;
    }

    if (registrationComplete) {
      return <RegistrationCompletePanel isPriorRegistration={true} />;
    }

    return (
      <>
        {isBusy && <BusyOverlay />}
        <Step1RegistrationPanel
          brand={brand}
          destPath={destPath}
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          countryOrRegion={countryOrRegion}
          setCountryOrRegion={setCountryOrRegion}
          role={role}
          setRole={setRole}
          availableRoles={availableRoles}
          roleType={roleType}
          setRoleType={setRoleType}
          school={school}
          setSchool={setSchool}
          discipline={discipline}
          setDiscipline={setDiscipline}
          availableDisciplines={availableDisciplines}
          setEmailPending={setEmailPending}
          setRegistrationComplete={setRegistrationComplete}
        />
      </>
    );
  };

  return (
    <BasePageUAM activeTab={2} brand={brand} destPath={destPath} needsDestPath={true}>
      {
        getPanel()
      }
    </BasePageUAM>
  );
};

RegistrationStep1Page.propTypes = {
  brand: PropTypes.object.isRequired,
  destPath: PropTypes.object.isRequired,
};

export default RegistrationStep1Page;